import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { getPurRecord, PurClientConfig, PurRecord } from "react-lib/apis/PUR/purApi";

import { orderDraftFromRequest, PurchaseDraft, requestOrderPreparationIssue } from "./purDrafts";
import { purErrorMessageId } from "./purPresentation";

interface PurRequestOrderPreparationProps {
  client: PurClientConfig;
  record: PurRecord;
  locked: boolean;
  onPrepared: (draft: PurchaseDraft) => void;
  onLockChange: (locked: boolean) => void;
}

const PurRequestOrderPreparation: React.FC<PurRequestOrderPreparationProps> = ({ client, record, locked, onPrepared, onLockChange }) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [errorId, setErrorId] = useState<string | null>(null);
  const active = useRef(true);
  const busy = useRef(false);
  const generation = useRef(0);
  const issue = requestOrderPreparationIssue(record, client.divisionId);
  useEffect(() => {
    active.current = true;
    generation.current += 1;
    setLoading(false); setErrorId(null);
    return () => {
      active.current = false; generation.current += 1;
      if (busy.current) { busy.current = false; onLockChange(false); }
    };
  }, [client, record.id, onLockChange]);

  const prepare = async () => {
    if (busy.current || locked || issue) return;
    const revision = generation.current;
    const isCurrent = () => active.current && revision === generation.current;
    busy.current = true; setLoading(true); setErrorId(null); onLockChange(true);
    try {
      const latest = await getPurRecord(client, "materialRequests", record.id);
      if (!isCurrent()) return;
      const freshIssue = latest.id === record.id ? requestOrderPreparationIssue(latest, client.divisionId) : "MEERP_MR_PO_SOURCE_ERROR";
      if (freshIssue) { setErrorId(freshIssue); return; }
      onPrepared(orderDraftFromRequest(latest, client.divisionId));
    } catch (error: unknown) {
      if (isCurrent()) setErrorId(error instanceof Error && error.message.startsWith("MEERP_MR_PO_") ? error.message : purErrorMessageId(error));
    } finally {
      if (isCurrent()) { busy.current = false; setLoading(false); onLockChange(false); }
    }
  };

  return <section aria-label={intl.formatMessage({ id: "MEERP_MR_PO_SECTION" })} className="card-meerp__request-order">
    <h4>{intl.formatMessage({ id: "MEERP_MR_PO_SECTION" })}</h4>
    <p>{intl.formatMessage({ id: "MEERP_MR_PO_PREPARE_NOTE" })}</p>
    <button className="card-meerp__button" type="button" disabled={locked || loading || Boolean(issue)} onClick={prepare}>{intl.formatMessage({ id: "MEERP_MR_PO_PREPARE" })}</button>
    {issue && <p>{intl.formatMessage({ id: issue })}</p>}
    {loading && <p role="status">{intl.formatMessage({ id: "MEERP_MR_PO_LOADING" })}</p>}
    {errorId && <p role="alert">{intl.formatMessage({ id: errorId })}</p>}
  </section>;
};

export default PurRequestOrderPreparation;
